import React, { useState, useEffect } from 'react';
import { optimizeProductImage } from '../utils/imageOptimizer';
import { apiFetch } from '../utils/api';

const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '28', '30', '32', '34', '36', '38', '40'];

const ProductForm = ({ 
  isEdit = false, 
  onSubmit, 
  loading = false,
  initialData = null,
  message = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    mrp: '',
    section: 'National Articles',
    category: '',
    sizes: [],
    moq: '',
    moqType: 'pcs',
    unitsPerSet: '',
    isAvailable: true,
  });

  const [imageItems, setImageItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Load initial data for edit mode
  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price || '',
        mrp: (initialData.mrp !== undefined && initialData.mrp !== null) ? initialData.mrp : '',
        section: initialData.section || 'National Articles',
        category: initialData.category?._id || initialData.category || '',
        sizes: initialData.sizes || [],
        moq: initialData.moq || '',
        moqType: initialData.moqType || 'pcs',
        unitsPerSet: initialData.unitsPerSet || '',
        isAvailable: initialData.isAvailable !== undefined ? initialData.isAvailable : true,
      });

      // Set existing images
      if (initialData.images && initialData.images.length > 0) {
        setImageItems(initialData.images.map(img => ({
          id: img.public_id || img.url || Math.random().toString(),
          url: img.url || img,
          isExisting: true,
          originalObject: img
        })));
      }
    }
  }, [isEdit, initialData]);

  // Fetch categories when section changes
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const res = await apiFetch('/api/categories');
        const data = await res.json();
        
        if (Array.isArray(data)) {
          const filtered = data.filter(cat => cat.section === formData.section);
          setCategories(filtered);
          
          // Reset category if it doesn't belong to the new section
          if (formData.category && !filtered.find(c => c._id === formData.category)) {
            setFormData(prev => ({ ...prev, category: '' }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch categories', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    if (formData.section) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.section]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      imageItems.forEach(item => {
        if (!item.isExisting && item.url.startsWith('blob:')) {
          URL.revokeObjectURL(item.url);
        }
      });
    };
  }, [imageItems]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSizeToggle = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const handleImagesSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (imageItems.length + files.length > 10) {
      alert('You can upload a maximum of 10 images.');
      e.target.value = '';
      return;
    }
    
    const newItems = await Promise.all(
      files.map(async (file) => {
        try {
          const optimizedFile = await optimizeProductImage(file);
          const objectUrl = URL.createObjectURL(optimizedFile);
          return {
            id: Math.random().toString(),
            url: objectUrl,
            isExisting: false,
            file: optimizedFile
          };
        } catch (err) {
          console.error('Image optimization failed, using original file:', err);
          const objectUrl = URL.createObjectURL(file);
          return {
            id: Math.random().toString(),
            url: objectUrl,
            isExisting: false,
            file: file
          };
        }
      })
    );
    
    setImageItems(prev => [...prev, ...newItems]);
    e.target.value = '';
  };

  const removeImageAt = (index) => {
    const itemToRemove = imageItems[index];
    if (!itemToRemove.isExisting && itemToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(itemToRemove.url);
    }
    setImageItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (draggedIndex === null || draggedIndex === index) return;

    const reorderedItems = [...imageItems];
    const [draggedItem] = reorderedItems.splice(draggedIndex, 1);
    reorderedItems.splice(index, 0, draggedItem);
    
    setImageItems(reorderedItems);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      alert('Product name is required');
      return;
    }
    if (!formData.description.trim()) {
      alert('Description is required');
      return;
    }
    if (!formData.price || formData.price <= 0) {
      alert('Valid price is required');
      return;
    }
    if (!formData.category) {
      alert('Category is required');
      return;
    }
    if (!formData.moq || formData.moq < 1) {
      alert('MOQ is required and must be at least 1');
      return;
    }
    if (formData.moqType === 'set' && (!formData.unitsPerSet || formData.unitsPerSet < 1)) {
      alert('Units per set is required when MOQ type is set');
      return;
    }
    if (imageItems.length === 0) {
      alert('At least one product image is required');
      return;
    }

    const data = new FormData();
    data.append('name', formData.name);
    data.append('description', formData.description);
    data.append('price', formData.price);
    data.append('mrp', formData.mrp !== undefined && formData.mrp !== null ? formData.mrp : '');
    data.append('section', formData.section);
    data.append('category', formData.category);
    data.append('moq', formData.moq);
    data.append('moqType', formData.moqType);
    data.append('isAvailable', formData.isAvailable);
    
    if (formData.moqType === 'set') {
      data.append('unitsPerSet', formData.unitsPerSet);
    }

    if (formData.sizes && formData.sizes.length > 0) {
      data.append('sizes', JSON.stringify(formData.sizes));
    }

    // Add existing images to keep
    const keptImages = imageItems
      .filter(item => item.isExisting)
      .map(item => item.originalObject);
    data.append('imagesKept', JSON.stringify(keptImages));

    // Add new images to upload
    const newItems = imageItems.filter(item => !item.isExisting);
    newItems.forEach(item => {
      data.append('images', item.file);
    });

    // Create simple ordered list representation for backend reconstruction
    const imagesOrder = imageItems.map(item => {
      if (item.isExisting) {
        return `existing:${item.url}`;
      } else {
        const index = newItems.indexOf(item);
        return `new:${index}`;
      }
    });
    data.append('imagesOrder', JSON.stringify(imagesOrder));

    onSubmit(data);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: 'var(--color-text-primary)', marginBottom: '5px', fontSize: '2.2rem' }}>
          {isEdit ? 'Edit Product' : 'Add New Product'}
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '1.05rem' }}>
          {isEdit ? 'Update product details below.' : 'Fill in the details below to add a new product to your store.'}
        </p>
      </div>

      {message && (
        <div style={{
          background: message.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
          color: message.type === 'error' ? '#fecaca' : '#bef264',
          border: `1px solid ${message.type === 'error' ? 'rgba(248,113,113,0.3)' : 'rgba(134,239,172,0.3)'}`,
          borderRadius: '10px',
          padding: '14px 18px',
          marginBottom: '20px',
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px' }}>
          {/* Product Information */}
          <div style={sectionCardStyle}>
            <h2 style={{ color: '#f97316', fontSize: '1.3rem', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.8rem' }}>📦</span>
              1. Product Information
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Product Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter product name"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Description <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter product description"
                rows="4"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Price <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: 'var(--color-text-secondary)', marginRight: '8px' }}>₹</span>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>MRP <span style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', fontWeight: 'normal' }}>(Optional)</span></label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: 'var(--color-text-secondary)', marginRight: '8px' }}>₹</span>
                  <input
                    type="number"
                    name="mrp"
                    value={formData.mrp}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

          </div>

          {/* Product Classification */}
          <div style={sectionCardStyle}>
            <h2 style={{ color: '#f97316', fontSize: '1.3rem', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.8rem' }}>🏷️</span>
              2. Product Classification
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Section <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                name="section"
                value={formData.section}
                onChange={handleInputChange}
                style={inputStyle}
              >
                <option value="National Articles">National Articles</option>
                <option value="Premium & Imported">Premium & Imported</option>
                <option value="Accessories">Accessories</option>
                <option value="Fragrance & Grooming">Fragrance & Grooming</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Category <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={!formData.section || loadingCategories}
                style={inputStyle}
              >
                <option value="">
                  {loadingCategories ? 'Loading categories...' : 'Select category'}
                </option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
              {!formData.section && (
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginTop: '6px' }}>Choose a section first to see categories</p>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px' }}>
          {/* Product Details */}
          <div style={sectionCardStyle}>
            <h2 style={{ color: '#f97316', fontSize: '1.3rem', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.8rem' }}>⚙️</span>
              3. Product Details
            </h2>

            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Available Sizes</label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                {sizes.map(size => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => handleSizeToggle(size)}
                    style={{
                      ...sizeChipStyle,
                      background: formData.sizes.includes(size) ? '#f97316' : 'var(--color-surface-2)',
                      borderColor: formData.sizes.includes(size) ? '#f97316' : 'var(--color-border-light, #3f3f46)',
                      color: formData.sizes.includes(size) ? '#000' : '#a1a1aa',
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Minimum Order Quantity (MOQ) <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="number"
                  name="moq"
                  value={formData.moq}
                  onChange={handleInputChange}
                  placeholder="Enter MOQ"
                  min="1"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>MOQ Type <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="moqType"
                      value="pcs"
                      checked={formData.moqType === 'pcs'}
                      onChange={handleInputChange}
                    />
                    <span style={{ color: 'var(--color-text-primary)' }}>PCS</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="moqType"
                      value="set"
                      checked={formData.moqType === 'set'}
                      onChange={handleInputChange}
                    />
                    <span style={{ color: 'var(--color-text-primary)' }}>SET</span>
                  </label>
                </div>
              </div>
            </div>

            {formData.moqType === 'set' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Units Per Set <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="number"
                  name="unitsPerSet"
                  value={formData.unitsPerSet}
                  onChange={handleInputChange}
                  placeholder="Enter units per set"
                  min="1"
                  style={inputStyle}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--color-border-light, rgba(255,255,255,0.06))' }}>
              <div>
                <label style={{ ...labelStyle, marginBottom: 0, color: '#f97316', fontWeight: 600 }}>Product Available</label>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginTop: '4px' }}>Make this product available for purchase</p>
              </div>
              <label style={{ display: 'flex', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="isAvailable"
                  checked={formData.isAvailable}
                  onChange={handleInputChange}
                  style={{ display: 'none' }}
                />
                <div style={{
                  width: '50px',
                  height: '28px',
                  background: formData.isAvailable ? '#f97316' : '#3f3f46',
                  borderRadius: '14px',
                  position: 'relative',
                  transition: 'background 0.3s',
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: '#fff',
                    borderRadius: '12px',
                    position: 'absolute',
                    top: '2px',
                    left: formData.isAvailable ? '24px' : '2px',
                    transition: 'left 0.3s',
                  }} />
                </div>
              </label>
            </div>
          </div>

          {/* Product Images */}
          <div style={sectionCardStyle}>
            <h2 style={{ color: '#f97316', fontSize: '1.3rem', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.8rem' }}>🖼️</span>
              4. Product Images
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <div style={{
                border: '2px dashed rgba(249,115,22,0.6)',
                borderRadius: '14px',
                padding: '40px 20px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, rgba(249,115,22,0.05), transparent)',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}>
                <input
                  type="file"
                  id="product-images"
                  accept="image/*"
                  multiple
                  onChange={handleImagesSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="product-images" style={{ cursor: 'pointer', display: 'block' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '10px' }}>☁️</div>
                  <h3 style={{ color: 'var(--color-text-primary)', marginBottom: '5px' }}>Upload Product Images</h3>
                  <p style={{ color: 'var(--color-text-secondary)', marginBottom: '12px' }}>Drag and drop images here, or click to select</p>
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>You can select multiple images</p>
                  <button
                    type="button"
                    onClick={() => document.getElementById('product-images').click()}
                    style={{
                      marginTop: '12px',
                      background: '#f97316',
                      color: '#000',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Choose Images
                  </button>
                </label>
              </div>

              {imageItems.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: '12px' }}>
                    Selected Images ({imageItems.length})
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '12px' }}>
                    {imageItems.map((item, idx) => (
                      <div
                        key={item.id}
                        onMouseEnter={() => setHovered(idx)}
                        onMouseLeave={() => setHovered(null)}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, idx)}
                        onDragEnd={handleDragEnd}
                        style={{
                          position: 'relative',
                          width: '100px',
                          height: '100px',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          background: '#0b0b0c',
                          border: draggedIndex === idx 
                            ? '2px dashed #f97316' 
                            : dragOverIndex === idx 
                              ? '2px solid #38bdf8' 
                              : '2px solid rgba(249,115,22,0.2)',
                          opacity: draggedIndex === idx ? 0.4 : 1,
                          cursor: draggedIndex === idx ? 'grabbing' : 'grab',
                          transition: 'all 0.2s ease',
                          transform: dragOverIndex === idx ? 'scale(1.05)' : 'scale(1)',
                        }}
                      >
                        <img
                          src={item.url}
                          alt={`preview-${idx}`}
                          draggable={false}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                        />
                        {hovered === idx && (
                          <button
                            type="button"
                            onClick={() => removeImageAt(idx)}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'rgba(0,0,0,0.7)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              color: 'var(--color-text-primary)',
                              cursor: 'pointer',
                              fontSize: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 10,
                            }}
                            title="Remove image"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '30px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              ...submitButtonStyle,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            <span style={{ marginRight: '8px' }}>📦</span>
            {loading ? (isEdit ? 'Updating...' : 'Publishing...') : (isEdit ? 'Update Product' : 'Publish Product')}
          </button>
        </div>
      </form>
    </div>
  );
};

const sectionCardStyle = {
  padding: '24px',
  background: 'var(--color-surface-1)',
  borderRadius: '14px',
  border: '1px solid var(--color-border-light, rgba(255,255,255,0.08))',
  boxShadow: '0 10px 30px var(--shadow-card, rgba(0,0,0,0.3))',
};

const labelStyle = {
  display: 'block',
  color: 'var(--color-text-secondary)',
  fontSize: '0.95rem',
  fontWeight: 600,
  marginBottom: '8px',
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-light, rgba(255,255,255,0.1))',
  background: 'var(--color-surface-2, #0f172a)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  fontSize: '1rem',
  fontFamily: 'inherit',
};

const sizeChipStyle = {
  padding: '10px 18px',
  borderRadius: '8px',
  border: '2px solid',
  cursor: 'pointer',
  fontWeight: 600,
  transition: 'all 0.2s',
};

const submitButtonStyle = {
  background: '#f97316',
  color: '#000',
  border: 'none',
  padding: '14px 32px',
  borderRadius: '10px',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  boxShadow: '0 6px 20px rgba(249,115,22,0.3)',
  transition: 'all 0.2s',
  display: 'flex',
  alignItems: 'center',
};

export default ProductForm;
